import { Divider } from "@mui/material";
import Grid from "@mui/material/Grid";
import React, { useContext } from "react";
import { GlobalContext } from "../../contexts/globalContextStore/GlobalContextStore";
import { EncoderStreamViewCard } from "../../encoder/components/streamviewer/EncoderStreamViewCard";
import { ZixiStreamViewCard } from "../../encoder/components/streamviewer/ZixiStreamViewCard";
import { getArchTypeFromStream, isFeed } from "../../encoder/js/encoder/utils/dashboardUtils";
import { EvisStreamViewCard } from "../../evis/components/streamviewer/EvisStreamViewCard";
import { CustomLabelCard } from "../reusableComponents/customLabelCard/CustomLabelCard";
import streamViewerStyles from "./streamViewerStyles";
/**
 * If the given string is a stream name, get it's architecture type and return the corresponding card.
 * @param {*} param0 
 * @returns 
 */
const ShowStreamViewCard = ({ streamName }) => {
    const { storeState } = useContext(GlobalContext);
    const { genericStates } = storeState;
    const { dashboardFeedQueryData } = genericStates;

    if (!isFeed(streamName)) {
        let thisArchType = getArchTypeFromStream(streamName, dashboardFeedQueryData.dbQueryData[0]);

        if (thisArchType === "zixi") {
            return <ZixiStreamViewCard key={streamName} streamName={streamName} />;
        } else if (thisArchType === "hw_encoder") {
            return <EncoderStreamViewCard key={streamName} streamName={streamName} />;
        } else {
            return <EvisStreamViewCard key={streamName} streamObject={streamName} />;
        }
    } else {
        return null;
    }
};

 /**
  * Encoder data display component; If custom label field not enabled, show the stream cards. Else show the stream cards with custom label field.
  * @param {*} props
  * @returns {JSX} JSX representation of Encoder data display component
  */
export default function StreamViewer(props) {
    const { storeState } = useContext(GlobalContext);
    const { evisStates, zixiStates, genericStates } = storeState;

    const { feedNameList } = zixiStates;
    const { checkedEvisIdsStreams } = evisStates;
    const { customLabelEnabled } = genericStates;  // Boolean for enabling the additional card
    const { evisSortMode } = evisStates;

    const getGroupId = (obj) => {
        if (obj.streamObject?.evisId) return obj.streamObject.evisId;
        return String(obj.order ?? '');
    };

    const getSuffix = (streamId, evisId) => {
        const idx = streamId.toLowerCase().indexOf(evisId.toLowerCase());
        if (idx === -1) return streamId;
        return streamId.substring(idx + evisId.length);
    };

    const getBaseName = (streamId, evisId) => {
        const suffix = getSuffix(streamId, evisId); // -<region>-<prim|secd|grp>-<unique_id>
        const parts = suffix.split('-').filter(Boolean);
        if (parts.length >= 2 && (parts[1] === 'prim' || parts[1] === 'secd')) {
            return parts[0] + '-_pair_';
        }
        return suffix;
    };

    const getStreamCategory = (streamId, evisId) => {
        const suffix = getSuffix(streamId, evisId);
        if (suffix.includes('-prim-')) return 0;
        if (suffix.includes('-secd-')) return 1;
        return 2;
    };
    const getGroupSortKey = (obj) => {
        if (obj.streamObject?.evisId) return obj.streamObject.evisId;
        return obj.feed || '';
    };  
    const sortedStreamObjects = [...feedNameList, ...checkedEvisIdsStreams].sort((a, b) => {
        const sortKeyA = getGroupSortKey(a);
        const sortKeyB = getGroupSortKey(b);
        const groupCompare = sortKeyA.localeCompare(sortKeyB);
        if (groupCompare !== 0) return groupCompare;

        if (evisSortMode === "interleaved" && a.streamObject && b.streamObject) {
            const evisIdA = a.streamObject.evisId || '';
            const evisIdB = b.streamObject.evisId || '';
            const catA = getStreamCategory(a.streamObject.stream_id, evisIdA);
            const catB = getStreamCategory(b.streamObject.stream_id, evisIdB);

            if (catA === 2 && catB !== 2) return 1;
            if (catA !== 2 && catB === 2) return -1;
            if (catA === 2 && catB === 2) return a.streamObject.stream_id.localeCompare(b.streamObject.stream_id);

            const baseA = getBaseName(a.streamObject.stream_id, evisIdA);
            const baseB = getBaseName(b.streamObject.stream_id, evisIdB);
            const baseCompare = baseA.localeCompare(baseB, undefined, { sensitivity: 'base' });
            if (baseCompare !== 0) return baseCompare;

            return catA - catB;
        }

    return 0;
});

    const renderStreamViewCard = (streamObject, index, key) => (
        <React.Fragment key={key}>
            {index > 0 && getGroupId(sortedStreamObjects[index - 1]) !== getGroupId(streamObject) && (
                <Divider sx={streamViewerStyles.feedDivider} />
            )}
            <Grid sx={streamViewerStyles.gridItem}>
                <ShowStreamViewCard streamName={streamObject.feed || streamObject.streamObject} />
            </Grid>
            {customLabelEnabled && (index === sortedStreamObjects.length - 1 || getGroupId(sortedStreamObjects[index + 1]) !== getGroupId(streamObject)) && (
                <Grid sx={streamViewerStyles.gridItem}>
                    <CustomLabelCard streamObject={streamObject} />
                </Grid>
            )}
        </React.Fragment>
    );
    return (
        <Grid container spacing={3} sx={{ padding: 3 }}>
            {sortedStreamObjects.map((streamObject, index) => {
                const key = streamObject.feed || streamObject.streamObject.stream_id;
                return renderStreamViewCard(streamObject, index, key);
            })}
        </Grid>
    );
}